<template>
    <div class="relative w-full">
        <TagsInputRoot
            :model-value="inputValue"
            :disabled="disabled"
            :delimiter="delimiter"
            :add-on-paste="addOnPaste"
            :add-on-blur="addOnBlur"
            :max="max"
            @update:model-value="setTags"
            class="min-h-input rounded-input focus-within:border-focus flex w-full flex-wrap items-center gap-2 border px-3 py-2 text-sm focus-within:ring-0"
            :class="{
                'border-error': errorMessage,
                'border-muval-gray-4 hover:border-muval-gray-3': !errorMessage,
                'bg-white': !disabled,
                'bg-muval-gray-5!': disabled,
            }"
        >
            <TagsInputItem
                v-for="tag in inputValue"
                :key="tag"
                :value="tag"
                class="flex items-center"
            >
                <slot
                    name="tag"
                    :tag="tag"
                    :remove="() => removeTag(tag)"
                >
                    <div
                        class="card flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-xs"
                        :class="isTagValid(tag) ? 'text-muval-gray-1' : 'border-error text-error border'"
                        :data-invalid="isTagValid(tag) ? undefined : ''"
                    >
                        <span
                            v-if="!hideDot"
                            :style="{ backgroundColor: getColourFromText(tag) }"
                            class="size-1.5 rounded-full"
                        />
                        <TagsInputItemText />
                        <TagsInputItemDelete class="text-muval-gray-3 hover:text-muval-gray-1 ml-1 cursor-pointer">
                            <SvgIcon
                                name="system-close"
                                class="-mx-1 size-3.5"
                            />
                        </TagsInputItemDelete>
                    </div>
                </slot>
            </TagsInputItem>
            <TagsInputInput
                @keydown.enter="commitDraftOnEnter"
                :placeholder="inputValue?.length ? '' : placeholder"
                :disabled="disabled"
                class="text-muval-gray-1 flex-1 bg-transparent text-sm placeholder-gray-400 outline-hidden"
            />
        </TagsInputRoot>
        <div
            v-if="label"
            class="pointer-events-none absolute -top-2 left-2 z-1 line-clamp-1 text-xs text-gray-400"
            :class="{ 'text-error': errorMessage }"
        >
            <span class="z-1 bg-white px-1">{{ label }}</span>
        </div>
        <div
            v-if="errorMessage"
            class="text-xxs text-error pointer-events-none absolute -bottom-5 w-full truncate whitespace-nowrap"
        >
            <span>{{ errorMessage }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { TagsInputRoot, TagsInputItem, TagsInputItemText, TagsInputItemDelete, TagsInputInput } from 'reka-ui';
import { useField } from 'vee-validate';
import { idGen } from '@utilities/idGen';
import { getColourFromText } from '@utilities/colors';

const props = withDefaults(
    defineProps<{
        modelValue?: string[] | null;
        vid?: string;
        label?: string | null;
        placeholder?: string;
        disabled?: boolean;
        delimiter?: string | RegExp;
        addOnPaste?: boolean;
        addOnBlur?: boolean;
        max?: number;
        hideDot?: boolean;
        isTagValid?: (tag: string) => boolean;
    }>(),
    {
        modelValue: () => [],
        vid: () => idGen(6),
        label: null,
        placeholder: 'Add tags...',
        disabled: false,
        delimiter: ',',
        addOnPaste: false,
        addOnBlur: false,
        max: 0,
        hideDot: false,
        isTagValid: () => true,
    },
);

const { value: inputValue, errorMessage } = useField<string[]>(() => props.vid, undefined, {
    syncVModel: true,
});

// Reka splits a paste with a trailing delimiter into an empty tag; drop those.
function setTags(tags: string[] | null) {
    inputValue.value = (tags ?? []).filter((tag) => tag.trim() !== '');
}

function removeTag(tagToRemove: string) {
    if (inputValue.value) {
        inputValue.value = inputValue.value.filter((tag) => tag !== tagToRemove);
    }
}

// Reka commits on Enter only after a tick, by which time the enclosing form has
// already been implicitly submitted, so Enter is handled here instead.
function commitDraftOnEnter(event: KeyboardEvent) {
    if (event.isComposing) {
        return;
    }

    event.preventDefault();

    const input = event.target as HTMLInputElement;
    const current = inputValue.value ?? [];
    const drafts = input.value
        .split(props.delimiter)
        .map((draft) => draft.trim())
        .filter((draft, index, all) => draft && !current.includes(draft) && all.indexOf(draft) === index);

    if (!drafts.length) {
        return;
    }

    inputValue.value = [...current, ...drafts].slice(0, props.max > 0 ? props.max : undefined);
    input.value = '';
}
</script>
